import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Plus, Check, Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { overlaysApi, facilitiesApi, queryKeys } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { OVERLAY_TEMPLATES, OverlayTemplate } from '@/lib/overlay-templates';

// ─── Apply Template Modal ──────────────────────────────────────────────────────
type Phase = 'configure' | 'applying' | 'done' | 'error';

function ApplyTemplateModal({
  template,
  defaultFacilityId,
  facilities,
  isAdmin,
  onClose,
}: {
  template: OverlayTemplate;
  defaultFacilityId: string;
  facilities: Array<{ facilityId: string; name: string }>;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>('configure');
  const [form, setForm] = useState({
    facilityId: defaultFacilityId,
    sheetId: 'A',
    name: template.name,
    description: template.description,
  });
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<number | null>(null);

  const INPUT = 'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

  async function runApply() {
    setPhase('applying');
    setError(null);
    try {
      setStatusMessage('Creating composition…');
      const desc = form.description.trim();
      const { composition } = await overlaysApi.createComposition({
        facilityId: form.facilityId,
        sheetId: form.sheetId,
        name: form.name.trim(),
        ...(desc ? { description: desc } : {}),
      });
      setCreatedId(composition.id);

      const sortedScenes = [...template.scenes].sort((a, b) => a.sortOrder - b.sortOrder);

      for (const sceneTemplate of sortedScenes) {
        setStatusMessage(`Creating scene "${sceneTemplate.name}"…`);
        const { scene } = await overlaysApi.createScene(composition.id, {
          name: sceneTemplate.name,
          sortOrder: sceneTemplate.sortOrder,
          isDefault: sceneTemplate.isDefault,
        });

        if (sceneTemplate.widgets.length > 0) {
          setStatusMessage(`Adding ${sceneTemplate.widgets.length} widget${sceneTemplate.widgets.length !== 1 ? 's' : ''} to "${sceneTemplate.name}"…`);
          await Promise.all(
            sceneTemplate.widgets.map(w => overlaysApi.createWidget(scene.id, w))
          );
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.overlays.all });
      setPhase('done');
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. A partial composition may exist in your Overlays list.');
      setPhase('error');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white text-gray-900 shadow-xl dark:bg-gray-900 dark:text-gray-100">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold">Apply Template</h2>
          {phase !== 'applying' && (
            <button onClick={onClose} className="rounded-md p-1 hover:bg-gray-100 dark:hover:bg-gray-800">
              <span className="text-xl leading-none">&times;</span>
            </button>
          )}
        </div>

        <div className="px-6 py-5">

          {/* ── Configure ── */}
          {phase === 'configure' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Creates a new overlay composition pre-loaded with <strong className="text-gray-700 dark:text-gray-200">{template.scenes.length} scenes</strong> and all widgets from the <strong className="text-gray-700 dark:text-gray-200">{template.name}</strong> template.
              </p>

              {isAdmin && (
                <div>
                  <label className="mb-1 block text-sm font-medium">Facility</label>
                  <select value={form.facilityId} onChange={e => setForm(f => ({ ...f, facilityId: e.target.value }))} className={INPUT}>
                    {facilities.map(f => <option key={f.facilityId} value={f.facilityId}>{f.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium">Sheet</label>
                <select value={form.sheetId} onChange={e => setForm(f => ({ ...f, sheetId: e.target.value }))} className={INPUT}>
                  {['A', 'B', 'C', 'D', 'E', 'F'].map(s => <option key={s} value={s}>Sheet {s}</option>)}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Composition Name <span className="text-red-500">*</span></label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={INPUT} />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Description</label>
                <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={INPUT} />
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500">
                Slideshow widgets will be created as empty placeholders — add your sponsor and image assets after applying.
              </p>
            </div>
          )}

          {/* ── Applying ── */}
          {phase === 'applying' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
              <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-200">{statusMessage}</p>
              <p className="text-xs text-gray-400">Please wait…</p>
            </div>
          )}

          {/* ── Done ── */}
          {phase === 'done' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-200">
                <strong>{form.name}</strong> created successfully with {template.scenes.length} scenes.
              </p>
              <p className="text-center text-xs text-gray-400">
                Open the editor to add your assets and customize widget content.
              </p>
            </div>
          )}

          {/* ── Error ── */}
          {phase === 'error' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <AlertCircle className="h-10 w-10 text-red-400" />
              <p className="text-center text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          {phase === 'configure' && (<>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={runApply} disabled={!form.name.trim() || !form.facilityId}>
              <Plus className="mr-1.5 h-4 w-4" />
              Apply Template
            </Button>
          </>)}

          {phase === 'done' && (<>
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={() => { onClose(); navigate('/overlays'); }}>
              Go to Overlays
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            {createdId && (
              <Button variant="outline" onClick={() => { onClose(); navigate(`/overlays/${createdId}`); }}>
                Open Editor
              </Button>
            )}
          </>)}

          {phase === 'error' && (<>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => setPhase('configure')}>Try Again</Button>
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─── Template Card ─────────────────────────────────────────────────────────────
function TemplateCard({ template, onApply }: { template: OverlayTemplate; onApply: () => void }) {
  const totalWidgets = template.scenes.reduce((sum, s) => sum + s.widgets.length, 0);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900">
      {/* 16:9 preview */}
      <div className="aspect-video w-full overflow-hidden bg-[#012249]">
        {template.renderPreview()}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{template.name}</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex-1">{template.description}</p>

        {/* Stats */}
        <div className="mt-3 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Layers className="h-3.5 w-3.5" />
            {template.scenes.length} scene{template.scenes.length !== 1 ? 's' : ''}
          </span>
          <span>·</span>
          <span>{totalWidgets} widgets</span>
        </div>

        {/* Tags */}
        <div className="mt-2 flex flex-wrap gap-1">
          {template.tags.map(tag => (
            <span key={tag} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              {tag}
            </span>
          ))}
        </div>

        {/* Scene list */}
        <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-800">
          {template.scenes.map(s => (
            <div key={s.name} className="flex items-center justify-between py-0.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                {s.isDefault && <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />}
                {s.name}
              </span>
              <span>{s.widgets.length} widgets</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 p-4 dark:border-gray-700">
        <Button className="w-full" onClick={onApply}>
          <Plus className="mr-1.5 h-4 w-4" />
          Apply Template
        </Button>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function TemplatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [selectedTemplate, setSelectedTemplate] = useState<OverlayTemplate | null>(null);

  const { data: facilitiesData } = useQuery({
    queryKey: queryKeys.facilities.list({ limit: 100 }),
    queryFn: () => facilitiesApi.list({ limit: 100 }),
    enabled: isAdmin,
  });

  const facilities = facilitiesData?.facilities ?? [];
  const defaultFacilityId = isAdmin
    ? (facilities[0]?.facilityId ?? '')
    : (user?.facilityId ?? '');

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Overlay Templates</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Ready-to-use overlay compositions. Apply a template to create a new composition pre-loaded with scenes and widgets.
        </p>
      </div>

      {OVERLAY_TEMPLATES.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-16 text-center dark:border-gray-700">
          <Layers className="mb-3 h-10 w-10 text-gray-400" />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No templates available</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {OVERLAY_TEMPLATES.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onApply={() => setSelectedTemplate(template)}
            />
          ))}
        </div>
      )}

      {selectedTemplate && (
        <ApplyTemplateModal
          template={selectedTemplate}
          defaultFacilityId={defaultFacilityId}
          facilities={facilities}
          isAdmin={isAdmin}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
    </div>
  );
}
